import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock } from 'lucide-react';
import { cn } from '@/utils/helpers';

// ============================================================================
// Difficulty Meter Icon - Bar chart style (matching planner style)
// ============================================================================
export const DifficultyMeterIcon = ({
  difficulty,
  size = 14,
}: {
  difficulty?: 'easy' | 'medium' | 'hard';
  size?: number;
}) => {
  // Match planner: use CSS variable for muted state
  const colors: Record<string, string> = { easy: '#22c55e', medium: '#eab308', hard: '#ef4444' };
  const color = difficulty ? colors[difficulty] : 'var(--icon-muted)';

  const w = size * 0.18, gap = size * 0.08;
  const heights = [size * 0.28, size * 0.48, size * 0.72];
  const totalW = w * 3 + gap * 2;
  const startX = (size - totalW) / 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      {[0, 1, 2].map(i => (
        <rect
          key={i}
          x={startX + i * (w + gap)}
          y={size * 0.88 - heights[i]}
          width={w}
          height={heights[i]}
          rx={w / 2}
          fill={color}
        />
      ))}
    </svg>
  );
};

// Difficulty config for meter style
export const difficultyMeterConfig = {
  easy: {
    label: 'Easy',
    level: 1 as const,
    activeColor: 'text-emerald-500 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  medium: {
    label: 'Medium',
    level: 2 as const,
    activeColor: 'text-yellow-500 dark:text-yellow-400',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    text: 'text-yellow-600 dark:text-yellow-400',
  },
  hard: {
    label: 'Hard',
    level: 3 as const,
    activeColor: 'text-red-500 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-600 dark:text-red-400',
  },
};

// ============================================================================
// Difficulty Meter Selector Component
// ============================================================================
interface DifficultyMeterSelectorProps {
  value?: 'easy' | 'medium' | 'hard';
  onChange: (difficulty: 'easy' | 'medium' | 'hard' | undefined) => void;
  size?: 'xs' | 'sm' | 'md';
}

export function DifficultyMeterSelector({
  value,
  onChange,
  size = 'md',
}: DifficultyMeterSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const hasValue = value !== undefined && value !== null;
  const currentDifficulty = value || 'easy';
  const config = difficultyMeterConfig[currentDifficulty];

  const sizeClasses = size === 'xs' ? 'h-5 w-5' : size === 'sm' ? 'h-6 w-6' : 'h-7 w-7';

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 140;
      const viewportWidth = window.innerWidth;
      const padding = 8;

      // Calculate left position - ensure dropdown stays within viewport
      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - padding) {
        left = Math.max(padding, rect.right - dropdownWidth);
      }

      setDropdownPos({
        top: rect.bottom + 4,
        left: left,
      });
    }
    setIsOpen(!isOpen);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden py-1"
      style={{ top: dropdownPos.top, left: dropdownPos.left, width: 100 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* None option first */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onChange(undefined);
          setIsOpen(false);
        }}
        className={cn(
          'w-full px-2.5 py-1.5 text-[11px] flex items-center gap-2 font-medium transition-colors',
          !hasValue ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        )}
      >
        <DifficultyMeterIcon size={14} />
        <span className="text-gray-700 dark:text-gray-300">None</span>
      </button>
      {/* Difficulty options: Easy, Medium, Hard */}
      {(['easy', 'medium', 'hard'] as const).map((diff) => {
        const isSelected = value === diff;
        return (
          <button
            key={diff}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onChange(diff);
              setIsOpen(false);
            }}
            className={cn(
              'w-full px-2.5 py-1.5 text-[11px] flex items-center gap-2 font-medium transition-colors',
              isSelected ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
            )}
          >
            <DifficultyMeterIcon difficulty={diff} size={14} />
            <span className="text-gray-700 dark:text-gray-300">{difficultyMeterConfig[diff].label}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  // Show "Add" button when no value - gray color
  if (!hasValue) {
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleOpen}
          title="Add difficulty"
          className={cn(
            'rounded-lg flex items-center justify-center shrink-0 transition-colors cursor-pointer',
            'text-gray-300 hover:text-gray-400 dark:text-gray-600 dark:hover:text-gray-500',
            sizeClasses
          )}
        >
          <DifficultyMeterIcon size={size === 'xs' ? 14 : size === 'sm' ? 18 : 20} />
        </button>
        {dropdownContent && createPortal(dropdownContent, document.body)}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        title={config.label}
        className={cn(
          'rounded-lg flex items-center justify-center shrink-0 transition-colors cursor-pointer',
          sizeClasses
        )}
      >
          <DifficultyMeterIcon difficulty={value} size={size === 'xs' ? 14 : size === 'sm' ? 18 : 20} />
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}

// ============================================================================
// Time Meter Display - Shows time as text
// ============================================================================
export const TimeMeterIcon = ({
  level: _level,
  activeColor: _activeColor,
  size = 'md',
  minutes
}: {
  level: 1 | 2 | 3 | 4;
  activeColor: string;
  size?: 'xs' | 'sm' | 'md';
  minutes?: number;
}) => {
  // Format time display - handles minutes, hours, days, weeks
  const formatTime = (mins: number) => {
    if (mins >= 10080) { // 1 week = 10080 mins
      const weeks = mins / 10080;
      return weeks % 1 === 0 ? `${weeks}w` : `${weeks.toFixed(1)}w`;
    }
    if (mins >= 1440) { // 1 day = 1440 mins
      const days = mins / 1440;
      return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
    }
    if (mins >= 60) {
      const hours = mins / 60;
      return hours % 1 === 0 ? `${hours}h` : `${hours.toFixed(1)}h`;
    }
    return `${mins}m`;
  };

  const displayTime = minutes ? formatTime(minutes) : '--';

  // Time color based on minutes
  const getColor = () => {
    if (!minutes) return 'text-gray-400';
    if (minutes < 60) return 'text-sky-500';       // Minutes - light blue
    if (minutes < 1440) return 'text-blue-500';    // Hours - blue
    return 'text-indigo-600';                       // Days/Weeks - indigo
  };

  const textSizeClass = size === 'xs' ? 'text-[9px]' : 'text-[11px]';

  return (
    <span className={`font-bold ${textSizeClass} ${getColor()} flex items-center justify-center`}>
      {displayTime}
    </span>
  );
};

// Time config for meter style - Always BLUE tones (light to dark based on duration)
export const timeMeterConfig = {
  '15': { label: '15 min', minutes: 15, level: 1 as const, activeColor: 'text-sky-400 dark:text-sky-400' },
  '30': { label: '30 min', minutes: 30, level: 1 as const, activeColor: 'text-sky-500 dark:text-sky-400' },
  '45': { label: '45 min', minutes: 45, level: 2 as const, activeColor: 'text-blue-500 dark:text-blue-400' },
  '60': { label: '1 hour', minutes: 60, level: 2 as const, activeColor: 'text-blue-500 dark:text-blue-400' },
  '90': { label: '1.5 hours', minutes: 90, level: 3 as const, activeColor: 'text-blue-600 dark:text-blue-500' },
  '120': { label: '2 hours', minutes: 120, level: 3 as const, activeColor: 'text-blue-600 dark:text-blue-500' },
  '180': { label: '3 hours', minutes: 180, level: 4 as const, activeColor: 'text-indigo-600 dark:text-indigo-500' },
  '240': { label: '4 hours', minutes: 240, level: 4 as const, activeColor: 'text-indigo-700 dark:text-indigo-600' },
};

// Helper function to format minutes to display string
const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

// ============================================================================
// Time Meter Selector Component
// ============================================================================
interface TimeMeterSelectorProps {
  value?: number;
  onChange: (minutes: number | undefined) => void;
  size?: 'xs' | 'sm' | 'md';
  disabled?: boolean; // When true, shows value but can't be edited (inactive appearance)
  isCumulative?: boolean; // When true, shows cumulative indicator
}

export function TimeMeterSelector({
  value,
  onChange,
  size = 'md',
  disabled = false,
  isCumulative = false,
}: TimeMeterSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const hasValue = value !== undefined && value !== null && value > 0;

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return; // Don't open dropdown when disabled
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 220;
      const viewportWidth = window.innerWidth;
      const padding = 8; // Padding from edge

      // Calculate left position - ensure dropdown stays within viewport
      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - padding) {
        // Align to right edge of button if it would overflow
        left = Math.max(padding, rect.right - dropdownWidth);
      }

      setDropdownPos({
        top: rect.bottom + 4,
        left: left,
      });
    }
    setIsOpen(!isOpen);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const timeCategories = [
    {
      label: 'Minutes',
      color: 'text-sky-500',
      options: [
        { minutes: 5, label: '5m' },
        { minutes: 10, label: '10m' },
        { minutes: 15, label: '15m' },
        { minutes: 20, label: '20m' },
        { minutes: 30, label: '30m' },
        { minutes: 45, label: '45m' },
      ]
    },
    {
      label: 'Hours',
      color: 'text-blue-500',
      options: [
        { minutes: 60, label: '1h' },
        { minutes: 90, label: '1.5h' },
        { minutes: 120, label: '2h' },
        { minutes: 180, label: '3h' },
        { minutes: 240, label: '4h' },
        { minutes: 360, label: '6h' },
        { minutes: 480, label: '8h' },
      ]
    },
    {
      label: 'Days',
      color: 'text-indigo-500',
      options: [
        { minutes: 1440, label: '1d' },
        { minutes: 2880, label: '2d' },
        { minutes: 4320, label: '3d' },
        { minutes: 7200, label: '5d' },
        { minutes: 10080, label: '1w' },
        { minutes: 20160, label: '2w' },
      ]
    }
  ];

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-2 overflow-hidden"
      style={{ top: dropdownPos.top, left: dropdownPos.left, width: 160 }}
      onClick={(e) => e.stopPropagation()}
    >
      {timeCategories.map((category, catIndex) => (
        <div key={category.label} className={catIndex > 0 ? 'mb-2' : 'mb-2'}>
          <div className="text-[9px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
            {category.label}
          </div>
          <div className="flex flex-wrap gap-1">
            {category.options.map((option) => {
              const isSelected = value === option.minutes;
              return (
                <button
                  key={option.minutes}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onChange(option.minutes);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'px-2 py-0.5 rounded text-[10px] font-medium transition-colors',
                    isSelected
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {/* Clear option */}
      {hasValue && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onChange(undefined);
            setIsOpen(false);
          }}
          className="w-full flex items-center justify-center gap-1 pt-2 mt-2 border-t border-gray-100 dark:border-gray-700 text-[10px] text-gray-400 dark:text-gray-500 hover:text-red-500"
        >
          <X className="w-3 h-3" />
          Clear
        </button>
      )}
    </div>
  ) : null;

  // Format time for display
  const formatTimeDisplay = (mins: number) => {
    if (mins >= 10080) {
      const weeks = mins / 10080;
      return weeks % 1 === 0 ? `${weeks}w` : `${weeks.toFixed(1)}w`;
    }
    if (mins >= 1440) {
      const days = mins / 1440;
      return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
    }
    if (mins >= 60) {
      const hours = mins / 60;
      return hours % 1 === 0 ? `${hours}h` : `${hours.toFixed(1)}h`;
    }
    return `${mins}m`;
  };

  const sizeClasses = size === 'xs'
    ? 'h-5 px-1 text-[10px]'
    : size === 'sm'
    ? 'h-5 sm:h-6 px-1 sm:px-1.5 text-[10px] sm:text-[11px]'
    : 'h-6 px-1.5 text-[11px]';

  const iconClasses = size === 'xs'
    ? 'w-2.5 h-2.5'
    : size === 'sm'
    ? 'w-2.5 h-2.5 sm:w-3 sm:h-3'
    : 'w-3 h-3';

  // Show "Add" button when no value
  if (!hasValue) {
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleOpen}
          title={disabled ? "Time locked by track" : "Add time estimate"}
          disabled={disabled}
          className={cn(
            'flex items-center gap-0.5 rounded-md font-medium transition-colors bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700',
            disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
            'text-gray-400 dark:text-gray-500',
            sizeClasses
          )}
        >
          <Clock className={iconClasses} />
          <span>0</span>
        </button>
        {!disabled && dropdownContent && createPortal(dropdownContent, document.body)}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        title={disabled ? `${formatMinutes(value)} (locked by track)` : (isCumulative ? `${formatMinutes(value)} (cumulative)` : formatMinutes(value))}
        disabled={disabled}
        className={cn(
          'flex items-center gap-0.5 rounded-md font-medium transition-colors bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          'text-blue-600 dark:text-blue-400',
          sizeClasses
        )}
      >
        <Clock className={iconClasses} />
        <span>{formatTimeDisplay(value)}</span>
        {isCumulative && !disabled && (
          <span className="text-[8px] text-indigo-400 font-medium">Σ</span>
        )}
      </button>

      {!disabled && dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}
